import React from 'react';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';

// ---------------------------------------------------------------------------
// Shared layout primitives so every WhatsApp tab has identical vertical rhythm,
// header alignment, and equal-height cards. Import these instead of hand-rolling
// a header/spacing in each sub-view.
// ---------------------------------------------------------------------------

// Stretches grid items so all cards in a row share the tallest card's height.
// Usage: <Grid size={{ xs: 12, md: 6 }} sx={waGridItem}> <Card .../> </Grid>
export const waGridItem = {
  display: 'flex',
  '& > *': { flex: 1, width: '100%', minWidth: 0 },
};

// Keeps a tab's content from collapsing shorter than the inbox, so switching
// tabs never makes the page jump in height.
export const WA_PANEL_MIN_H = { xs: 'auto', md: 'calc(100dvh - 300px)' };

// True on phone-sized screens. Use to drive full-screen dialogs, stacked rows, etc.
export const useWaMobile = () => useMediaQuery((t) => t.breakpoints.down('sm'));

// Tracks live visual viewport height (vital for soft keyboard popping up in PWA / iOS mobile)
export const useVisualViewportHeight = () => {
  const [vpHeight, setVpHeight] = React.useState(null);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    const vv = window.visualViewport;
    const update = () => {
      const h = Math.round(vv.height);
      setVpHeight((prev) => (prev !== null && Math.abs(prev - h) <= 1 ? prev : h));
    };
    update();
    vv.addEventListener('resize', update);
    return () => {
      vv.removeEventListener('resize', update);
    };
  }, []);

  return vpHeight;
};

// Detects if app is running as an installed PWA in standalone display mode
export const useIsPwa = () => {
  const [isPwa, setIsPwa] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    setIsPwa(Boolean(isStandalone));
  }, []);
  return isPwa;
};

// Spread onto every <Dialog> in the module so popups go full-screen on phones
// (no cramped, edge-touching cards) and become comfortable rounded cards on
// larger screens. Pass the desired desktop maxWidth. Usage:
//   const mobile = useWaMobile();
//   <Dialog {...waDialogProps(mobile, 'md')}> … </Dialog>
export const waDialogProps = (mobile, maxWidth = 'sm') => ({
  fullScreen: mobile,
  maxWidth,
  fullWidth: true,
  PaperProps: { sx: { borderRadius: { xs: 0, sm: 3 } } },
});

// Spread onto <DialogActions sx={waDialogActionsSx}> so the buttons stack and go
// full-width on phones (easy thumb targets) and sit inline on larger screens.
export const waDialogActionsSx = {
  px: { xs: 2, sm: 3 },
  py: 2,
  gap: 1,
  flexDirection: { xs: 'column-reverse', sm: 'row' },
  '& > button': { width: { xs: '100%', sm: 'auto' }, m: { xs: 0, sm: undefined } },
  '& > :not(style) ~ :not(style)': { ml: { xs: 0, sm: 1 } },
};

// Standard section header: title + subtitle on the left, optional action on the
// right. Identical margins/sizing across every tab.
const WaSectionHeader = ({ title, subtitle, action }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    justifyContent="space-between"
    alignItems={{ xs: 'flex-start', sm: 'center' }}
    flexWrap="wrap"
    gap={1.5}
    sx={{ mb: 2.5, minHeight: 40 }}
  >
    <Box sx={{ minWidth: 0, width: '100%', flex: 1 }}>
      <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.3 }}>{title}</Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>{subtitle}</Typography>
      )}
    </Box>
    {action && <Box sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>{action}</Box>}
  </Stack>
);

export default WaSectionHeader;
